import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Clock, ArrowRight } from "lucide-react";
import { RECIPES, FLAVORS } from "../lib/content";
import { ShareButton } from "../components/ShareButton";

const TABS = [
  { id: "all", label: "All" },
  { id: "cocktail", label: "Cocktails" },
  { id: "brew", label: "Brews" },
];

export default function RecipesPage() {
  const [tab, setTab] = useState("all");
  const list = RECIPES.filter((r) => tab === "all" || r.type === tab);

  return (
    <main data-testid="recipes-page" className="pt-28 pb-24 px-5 md:px-10">
      <div className="max-w-[1200px] mx-auto">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-ember mb-4">Recipe Hub</p>
        <h1 className="font-display font-black uppercase tracking-tight text-cream text-4xl sm:text-5xl lg:text-6xl leading-[0.92]">
          Cause delicious <span className="font-serif italic font-normal lowercase text-ember">trouble</span>
        </h1>
        <p className="font-mono text-sm text-cream/75 mt-5 max-w-lg leading-relaxed">
          Every recipe here starts with an air-roasted Gremlin bean. Pick your flavor, follow the steps, and make it your own.
        </p>

        <div className="flex gap-2 mt-10">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`recipes-tab-${t.id}`}
              className={`font-mono text-xs uppercase tracking-widest px-5 py-2.5 rounded-full border transition-colors ${
                tab === t.id ? "bg-ember text-void border-ember" : "border-white/15 text-cream/75 hover:border-ember/60 hover:text-ember"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-10">
          {list.map((r, i) => {
            const flavor = FLAVORS.find((f) => f.id === r.flavorId);
            return (
              <motion.article
                key={r.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: (i % 2) * 0.08, ease: [0.16, 1, 0.3, 1] }}
                data-testid={`recipe-card-${r.id}`}
                className="border border-white/10 rounded-lg bg-ash overflow-hidden"
              >
                <div className="h-1.5" style={{ background: flavor?.accent }} />
                <div className="p-7">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-[10px] uppercase tracking-widest bg-ember text-void px-2.5 py-1 rounded-full">{r.type}</span>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-[11px] text-cream/65 flex items-center gap-1.5"><Clock size={12} /> {r.time}</span>
                      <ShareButton kind="recipe" slug={r.id} title={r.name} compact />
                    </div>
                  </div>
                  <h2 className="font-display font-extrabold uppercase tracking-tight text-cream text-2xl mt-4">{r.name}</h2>
                  <Link
                    to={`/flavor/${r.flavorId}`}
                    data-testid={`recipe-flavor-link-${r.id}`}
                    className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-ember mt-2 hover:text-cream transition-colors"
                  >
                    Made with {flavor?.name} <ArrowRight size={12} />
                  </Link>

                  <div className="mt-6">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-cream/55 mb-2">Ingredients</p>
                    <ul className="space-y-1.5">
                      {r.ingredients.map((ing) => (
                        <li key={ing} className="font-mono text-xs text-cream/70 flex gap-2.5">
                          <span className="w-1 h-1 rounded-full bg-ember mt-1.5 shrink-0" /> {ing}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="mt-6">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-cream/55 mb-2">Method</p>
                    <ol className="space-y-2">
                      {r.steps.map((s, si) => (
                        <li key={`${r.id}-step-${si}`} className="font-mono text-xs text-cream/70 flex gap-3 leading-relaxed">
                          <span className="font-display font-black text-ember shrink-0">{String(si + 1).padStart(2, "0")}</span> {s}
                        </li>
                      ))}
                    </ol>
                  </div>

                  <p className="font-mono text-[11px] text-ember mt-6 border-l-2 border-ember pl-3 leading-relaxed">{r.note}</p>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </main>
  );
}
